import React, { useRef, useState } from 'react';
import {
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
  IonMenuToggle,
  IonAccordion,
  IonAccordionGroup,
  IonContent,
} from '@ionic/react';
import { useHistory, useLocation } from 'react-router-dom';
import { closeOutline, settingsOutline } from 'ionicons/icons';
import './Navigation.css';
import Message from '../Message/Message';
import { useUserState } from '../../hooks/context/useUserState';
import { useLogout } from '../../api/hooks/useLogout';
import { getIsMobile } from '../../utils/helpers';
import { SET_ENDPOINT_PAGE_MANUAL_ENTERING } from '../../constants';
import useApiErrorHandling from '../../hooks/useApiErrorHandling';
import { useCfgParam } from '../../hooks/useCfgParam';
import Button from '../Button/Button';
import Loading from '../Loading/Loading';
import { useTranslate } from '../../hooks/context/useTranslate';
import SupportModal from '../SupportModal/SupportModal';
import useRoutes from '../../hooks/useRoutes';
import { GlobalLocationStateType } from '../../types/types';
import { useVenueTitle } from '../../hooks/useVenueTitle';
import Sidebar from '../Sidebar/Sidebar';
import { useDatasetContext } from '../../hooks/context/useDatasetContext';
import { useFormTokenChainContext } from '../../hooks/context/useFormTokenChainContext';
// import LanguageSwitcherAccordion from '../LanguageSwitcherAccordion/LanguageSwitcherAccordion';

const isMobile = getIsMobile();

const Navigation = () => {
  const { pathname } = useLocation<GlobalLocationStateType>();
  const {
    state: { username, menuItems },
    dispatch,
  } = useUserState();
  const { dataset } = useDatasetContext();
  const { getActualTokenFromUrl, setSmallExpirationTimeToTokenChain } =
    useFormTokenChainContext();
  const appName = useCfgParam('appName');
  const { logout, isPending } = useLogout();
  const history = useHistory();
  const handleApiError = useApiErrorHandling();
  const { t } = useTranslate();
  const routes = useRoutes();
  const menuRef = useRef<HTMLIonMenuElement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { title: venueTitle, isLoading: venueTitleLoading } = useVenueTitle();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleEndpointSettingsClick = () => {
    dispatch({ type: SET_ENDPOINT_PAGE_MANUAL_ENTERING, payload: false });
    history.push(routes.setEndpoint());
  };

  const handleMenuItemClick = () => {
    const token = getActualTokenFromUrl();
    if (token) {
      setSmallExpirationTimeToTokenChain(token.id);
    }
    void menuRef.current?.close();
  };

  return (
    <IonMenu ref={menuRef} contentId="main" type="overlay">
      {isPending && <Loading message={t('loggingOut')} />}
      <SupportModal
        isOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
      />
      <div className="sidebar-header">
        <div className="logo-container">
          <div className="close-button">
            <IonMenuToggle autoHide={false}>
              <IonIcon color="primary" icon={closeOutline} />
            </IonMenuToggle>
          </div>
          <img src="/images/logo.svg" alt={appName} />
        </div>
        {username && (
          <div className="user-info">
            <div className="branch-name">
              <img src="/images/logo-mobile.svg" alt="Septim logo" />
              <h2 aria-live="polite" aria-busy={venueTitleLoading}>
                {venueTitle}
              </h2>
            </div>
            {username && (
              <div className="table">
                <div>
                  <div>
                    <Message id="username" />:
                  </div>
                  <div>{username}</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <IonContent>
        {username && menuItems && menuItems.length !== 0 && (
          <div className="mobile-app-navigation">
            <Sidebar
              menuItems={menuItems}
              peersets={dataset?.peersets}
              onMenuItemClick={handleMenuItemClick}
            />
          </div>
        )}
        <IonList id="inbox-list">
          {/* TODO: Enable when SQL will support multiple languages */}
          {/* <LanguageSwitcherAccordion /> */}
          {isMobile && (
            <IonAccordionGroup>
              <IonAccordion value="first">
                <IonItem slot="header">
                  <IonIcon slot="start" icon={settingsOutline} />
                  <IonLabel>
                    <Message id="settings" />
                  </IonLabel>
                </IonItem>
                <div slot="content">
                  <IonMenuToggle autoHide={false}>
                    <IonItem
                      button
                      onClick={handleEndpointSettingsClick}
                      className={
                        pathname.startsWith(routes.setEndpoint())
                          ? 'selected'
                          : ''
                      }
                      lines="none"
                    >
                      <IonLabel>
                        <Message id={'endpoint'} />
                      </IonLabel>
                    </IonItem>
                  </IonMenuToggle>
                </div>
              </IonAccordion>
            </IonAccordionGroup>
          )}
        </IonList>
        {username && (
          <Button
            className="logout-btn"
            color="dark"
            fill="clear"
            onClick={handleLogout}
            small
          >
            <Message id="logOut" />
          </Button>
        )}
      </IonContent>
      <div className="support-btn-container ion-padding">
        <Button onClick={() => setIsModalOpen(true)}>
          <Message id={'support'} />
        </Button>
      </div>
    </IonMenu>
  );
};

export default Navigation;
